import { useEffect, useState } from 'react';
import CustomerChatOpenButton from 'components/CustomerChatOpenButton/CustomerChatOpenButton';
import CustomerChatLateralModal from 'components/CustomerChatLateralModal/CustomerChatLateralModal';
import { MENTION_TYPES } from 'types/dashboard.types';
import useOpenCustomerChatFromLocation from 'hooks/useOpenCustomerChatFromLocation';
import { isCustomerChatEnabledForOrganization } from 'utils/customerChat.utils';
import LogisticServiceDetailsTabs, {
  IlogisticServiceDetailsTab
} from '../LogisticServiceDetailsTabs/LogisticServiceDetailsTabs';
import { getLogisticServicesTrelloDataByState } from '../../../../../../services/services';
import {
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../types/services';
import { displayErrorNotification } from '../../../../../../utils/displayNudosStandardNotifications';
import {
  displayConfigurationTransitionBlockedNotification,
  evaluateLogisticServiceActiveTransition,
  GO_TO_CONFIGURATIONS_BUTTON_TEXT,
  LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE
} from '../../../../../../utils/productConfigurationSummary.utils';
import LogisticServiceHeaderInformationSectionSkeletonLoader from './LogisticServiceHeaderInformationSectionSkeletonLoader';
import LogisticsServicesTrelloModals from '../../../../../services/components/ModalsLogisticsService/LogisticsServicesTrelloModals';
import { ILogisticServiceHistory } from 'types/suscription/history.types';
import { LogisticServiceLastUpdate } from '../LogisticServiceLastUpdate/LogisticServiceLastUpdate';
import { OrderSummaryCard } from 'views/orders/OrderDetailInternalCard/Components/OrderSummaryCard/OrderSummaryCard';
import { formatDataForLogisticServiceSummaryCard } from './utils';
import LogisticServiceStatusSelect from '../../LogisticServiceStatusSelect/LogisticServiceStatusSelect';
import { BordResponsibleUser } from 'components/NudosComponents';
import { LogisticServiceSummaryDetails } from '../LogisticServiceSummaryDetails/LogisticServiceSummaryDetails';
import { OrderCommentsBox } from 'views/orders/OrderDetailInternalCard/Components/OrderCommentsBox/OrderCommentsBox';
import { useBordResponsibleUserDataForLogisticService } from 'components/NudosComponents/BordReponsibleUser/hooks/useBordResponsibleUserDataForLogisticService';
import useOnlyDevelopers from 'state/setOnlyDevelopers';

import './LogisticServiceHeaderInformationSection.scss';

const LogisticServiceHeaderInformationSection = ({
  logisticServiceData,
  logisticServiceHistoryData,
  handleTabSelectionCallback,
  showSkeletonLoader,
  updateDataCallback,
  updateWithoutLoader
}: {
  logisticServiceData: IlogisticServiceDetails;
  logisticServiceHistoryData: ILogisticServiceHistory[];
  handleTabSelectionCallback: (newTabData: IlogisticServiceDetailsTab) => void;
  showSkeletonLoader: boolean;
  updateDataCallback: () => Promise<void>;
  updateWithoutLoader: () => Promise<void>;
}) => {
  const { id, title, statusName, manualQuote } = logisticServiceData;
  const isLogisticServiceCancelled = statusName === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED;

  // Estados para controlar los modales
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showDelayedModal, setShowDelayedModal] = useState<boolean>(false);
  const [showActiveModal, setShowActiveModal] = useState<boolean>(false);
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);
  const [showInProcessModal, setShowInProcessModal] = useState<boolean>(false);
  const [selectedLogisticServiceDataForModal, setSelectedLogisticServiceDataForModal] =
    useState<IlogisticServiceTrelloCardData>();

  const { onlyDevelopers, isOnlyDevsLogistics } = useOnlyDevelopers();
  const shouldOpenCustomerChatFromUrl = useOpenCustomerChatFromLocation();
  const [customerChatOpen, setCustomerChatOpen] = useState(false);
  const isCustomerChatEnabled = isCustomerChatEnabledForOrganization(
    logisticServiceData?.organization?.organizationId
  );

  const { isLoading, assignResponsible, deleteResponsible, getResponsibleUserList, responsibleUserList, userList } =
    useBordResponsibleUserDataForLogisticService(logisticServiceData?.id!);

  /**
   * Maneja el cambio de estado del servicio logístico
   * @param newStatus - Nuevo estado seleccionado
   */
  const validateConfirmedStatus = () => {
    const originViewedChanges = logisticServiceData.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = logisticServiceData.addressIssue?.destination?.viewedChanges;
    const originAddressEditedByUser = logisticServiceData.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEditedByUser = logisticServiceData.addressIssue?.destination?.editedBy === 'user';
    const originAddressEditedByUserAndNotViewed = originAddressEditedByUser && !originViewedChanges;
    const destinationAddressEditedByUserAndNotViewed = destinationAddressEditedByUser && !destinationViewedChanges;

    if (originAddressEditedByUserAndNotViewed || destinationAddressEditedByUserAndNotViewed) {
      displayErrorNotification({
        customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
      });
      return false;
    }

    if (!logisticServiceData.quoteDocumentUrl) {
      displayErrorNotification({
        customJSXMessage: <>Se necesita una cotización para mover el servicio logístico a confirmado</>
      });
      return false;
    }

    return true;
  };

  const validateInProcessStatus = () => {
    const originViewedChanges = logisticServiceData.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = logisticServiceData.addressIssue?.destination?.viewedChanges;
    const originAddressEdited = logisticServiceData.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEdited = logisticServiceData.addressIssue?.destination?.editedBy === 'user';

    if ((!originViewedChanges && originAddressEdited) || (!destinationViewedChanges && destinationAddressEdited)) {
      displayErrorNotification({
        customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
      });
      return false;
    }

    return true;
  };

  const validateActiveStatus = async (): Promise<boolean> => {
    if (!logisticServiceData.id) return false;
    if (!isOnlyDevsLogistics) return true;

    try {
      const { isBlocked } = await evaluateLogisticServiceActiveTransition(logisticServiceData.id);

      if (!isBlocked) return true;

      displayConfigurationTransitionBlockedNotification({
        displayErrorNotification,
        mainMessage: LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE,
        button1Text: GO_TO_CONFIGURATIONS_BUTTON_TEXT,
        button1Action: () => {
          window.location.href = `/logistic-service/details/${logisticServiceData.id}?initialTab=2`;
        }
      });

      return false;
    } catch {
      displayErrorNotification();
      return false;
    }
  };

  const handleStatusChange = async (newStatus: LOGISTIC_SERVICE_STATUS_NAME, isLate?: boolean | null) => {
    const { CANCELLED, CONFIRMED, ACTIVE, DELIVERED, INPROCESS, DELAYED, TOCONFIRMED } = LOGISTIC_SERVICE_STATUS_NAME;

    let enrichedData;
    try {
      if (logisticServiceData.id) {
        const currentStatus = isLate ? DELAYED : statusName?.toLowerCase() || CONFIRMED;
        const [enrichedServiceData] = await getLogisticServicesTrelloDataByState([
          `/logistic-service/${currentStatus}?searchedId=${logisticServiceData.id}`,
          currentStatus
        ]);
        enrichedData = enrichedServiceData;
      }
    } catch (error) {
      enrichedData = null;
    }

    switch (newStatus) {
      case TOCONFIRMED:
      case CONFIRMED:
        if (!validateConfirmedStatus()) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowConfirmModal(true);
        break;

      case INPROCESS:
        if (!validateInProcessStatus()) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowInProcessModal(true);
        break;

      case ACTIVE:
        if (!(await validateActiveStatus())) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowActiveModal(true);
        break;

      case DELIVERED:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowDeliveredModal(true);
        break;

      case CANCELLED:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowCancelModal(true);
        break;

      case DELAYED:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowDelayedModal(true);
        break;

      default:
        break;
    }
  };

  const clearAllModals = async () => {
    setShowCancelModal(false);
    setShowConfirmModal(false);
    setShowDelayedModal(false);
    setShowActiveModal(false);
    setShowDeliveredModal(false);
    setShowInProcessModal(false);
    setSelectedLogisticServiceDataForModal(undefined);

    await updateDataCallback();
  };

  const summaryCardData = formatDataForLogisticServiceSummaryCard(logisticServiceData);

  useEffect(() => {
    if (shouldOpenCustomerChatFromUrl && isCustomerChatEnabled) setCustomerChatOpen(true);
  }, [shouldOpenCustomerChatFromUrl, isCustomerChatEnabled]);

  useEffect(() => {
    if (onlyDevelopers) {
      getResponsibleUserList();
    }
  }, [getResponsibleUserList]);

  if (showSkeletonLoader) return <LogisticServiceHeaderInformationSectionSkeletonLoader />;

  return (
    <>
      <LogisticsServicesTrelloModals
        selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
        setSelectedLogisticServiceDataForModal={setSelectedLogisticServiceDataForModal}
        closeAndRestoreList={clearAllModals}
        cancelModalData={{
          showModal: showCancelModal,
          closeModal: clearAllModals
        }}
        confirmModalData={{
          showModal: showConfirmModal,
          closeModal: clearAllModals
        }}
        processModalData={{
          showModal: showInProcessModal,
          closeModal: clearAllModals
        }}
        delayedModalData={{
          showModal: showDelayedModal,
          closeModal: clearAllModals
        }}
        deliveredModalData={{
          showModal: showDeliveredModal,
          closeModal: clearAllModals
        }}
        activeModalData={{
          showModal: showActiveModal,
          closeModal: clearAllModals
        }}
      />
      <div className="logisticServiceHeaderSummaryCardContainer">
        <OrderSummaryCard
          id={logisticServiceData?.id!}
          idLabel="SL"
          orgData={summaryCardData?.orgData}
          userData={summaryCardData?.userData}
          subtitle={
            <LogisticServiceSummaryDetails
              manual={logisticServiceData.manualQuote}
              logisticType={logisticServiceData?.logisticType}
              origin={logisticServiceData?.origin}
              destination={logisticServiceData?.destination}
            />
          }
          responsibleUsers={
            <BordResponsibleUser
              bordResponsibleUserSearchDropdownCustomClassName="bordResponsibleUserSearchDropdownRightPosition"
              addResponsiblesButtonClassName="outlineAddResponsiblesButton"
              plusButtonIconClassName="outlinedPlusButton"
              allUsersList={userList}
              logisticServiceId={logisticServiceData?.id}
              responsibleUsersList={responsibleUserList}
              handleAssignResponsible={assignResponsible}
              handleDeleteResponsible={deleteResponsible}
              showResponsibleUsersSkeletonLoader={isLoading}
              isLoading={isLoading}
            />
          }
          status={
            <LogisticServiceStatusSelect
              currentStatus={statusName as LOGISTIC_SERVICE_STATUS_NAME}
              onStatusChange={handleStatusChange}
              isLate={logisticServiceData?.isLate || logisticServiceData?.delayed}
            />
          }
          lastUpdate={
            <LogisticServiceLastUpdate
              displayStatus={false}
              statusName={statusName as LOGISTIC_SERVICE_STATUS_NAME}
              logisticServiceHistoryData={logisticServiceHistoryData}
              onStatusChange={handleStatusChange}
              isLate={logisticServiceData?.isLate || logisticServiceData?.delayed}
            />
          }
        />
        <OrderCommentsBox
          organizationId={logisticServiceData?.organization?.organizationId!}
          logisticServiceId={logisticServiceData.id!}
          onUploadComment={updateWithoutLoader}
        />
      </div>
      <div className="logisticServiceDetailsTabsRow">
        <LogisticServiceDetailsTabs
          logisticServiceData={logisticServiceData || {}}
          logisticServiceHistoryData={logisticServiceHistoryData}
          handleTabSelectionCallback={handleTabSelectionCallback}
          updateDataCallback={updateDataCallback}
          updateWithoutLoader={updateWithoutLoader}
        />
        {isCustomerChatEnabled && (
          <CustomerChatOpenButton onClick={() => setCustomerChatOpen(true)} />
        )}
      </div>
      {logisticServiceData?.id && isCustomerChatEnabled && (
        <CustomerChatLateralModal
          open={customerChatOpen}
          onClose={() => setCustomerChatOpen(false)}
          mode={MENTION_TYPES.LOGISTIC_SERVICE}
          entityId={logisticServiceData.id}
          onAfterSend={updateWithoutLoader}
          isEntityCancelled={isLogisticServiceCancelled}
        />
      )}
    </>
  );
};

export default LogisticServiceHeaderInformationSection;
